import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  message,
  Divider,
  Upload,
  Spin,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  UploadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import useRequest from "../hooks/useRequest";
import ImgCrop from "antd-img-crop";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const BANNER_MAX_BYTES = 5 * 1024 * 1024;
const BANNER_ASPECT = 16 / 9;
const BANNER_ASPECT_EPS = 0.02;

function readImageNaturalSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to read image"));
    };
    img.src = url;
  });
}

function BannerManager() {
  const { request } = useRequest();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [activeTab, setActiveTab] = useState("user");

  const validateBannerBeforeCrop = useCallback((file) => {
    if (file.size > BANNER_MAX_BYTES) {
      message.error("Image must be 5MB or smaller");
      return false;
    }
    return true;
  }, []);

  // ----------------------------------
  // FETCH BANNERS
  // ----------------------------------
  const fetchBanners = () => {
    setLoading(true);

    request({
      url: `/api/admin/banners?type=${activeTab}`,
      method: "GET",
      onSuccess: (res) => {
        setLoading(false);
        setBanners(res?.data || []);
      },
      onError: () => {
        setLoading(false);
        message.error("Failed to fetch banners");
      },
    });
  };

  useEffect(() => {
    fetchBanners();
  }, [activeTab]);

  // ----------------------------------
  // CREATE BANNER
  // ----------------------------------
  const handleCreate = (values) => {
    if (!file) {
      message.error("Please upload banner image");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("title", values.title || "");
    formData.append("redirect_url", values.link || "");
    formData.append("is_active", values.status ? 1 : 0);

    setLoading(true);

    request({
      url: `/api/admin/banners/create?type=${activeTab}`,
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: () => {
        setLoading(false);
        message.success("Banner created successfully");
        form.resetFields();
        setFile(null);
        setModalOpen(false);
        fetchBanners();
      },
      onError: () => {
        setLoading(false);
        message.error("Failed to create banner");
      },
    });
  };

  // ----------------------------------
  // DELETE
  // ----------------------------------
  const handleDelete = (bannerId) => {
    setLoading(true);

    request({
      url: `/api/admin/banners/${bannerId}/delete?type=${activeTab}`,
      method: "DELETE",
      onSuccess: () => {
        setLoading(false);
        message.success("Banner deleted");
        fetchBanners();
      },
      onError: () => {
        setLoading(false);
        message.error("Failed to delete banner");
      },
    });
  };

  // ----------------------------------
  // STATUS UPDATE
  // ----------------------------------
  const handleStatusChange = (bannerId, value) => {
    request({
      url: `/api/admin/banners/${bannerId}/status?type=${activeTab}`,
      method: "POST",
      data: { is_active: value },
      onSuccess: () => {
        message.success("Status updated");
        fetchBanners();
      },
      onError: () => {
        message.error("Failed to update status");
      },
    });
  };

  // ----------------------------------
  // REORDER
  // ----------------------------------
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(banners);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    setBanners(items);

    request({
      url: `/api/admin/banners/reorder?type=${activeTab}`,
      method: "POST",
      data: {
        order: items.map((b) => b.id),
      },
      onSuccess: () => {
        message.success("Banner order updated");
        fetchBanners();
      },
      onError: () => {
        message.error("Failed to reorder banners");
      },
    });
  };

  return (
    <div
    >
      {/* HEADER */}
      <Card
        style={{
          marginBottom: 25,

        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>
              <PictureOutlined /> Banner Management
            </Title>
            <Text type="secondary">
              Manage promotional banners across platform
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
              style={{ borderRadius: 10, fontWeight: 500, paddingInline: 20 }}
            >
              Upload Banner
            </Button>
          </Col>
        </Row>
      </Card>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          { key: "user", label: "User Banners" },
          { key: "merchant", label: "Merchant Banners" },
        ]}
      />
      {/* GRID */}
      <Spin spinning={loading}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="banners">
            {(provided) => (
              <Row
                gutter={[24, 24]}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {banners.map((banner, index) => (
                  <Draggable
                    key={banner.id}
                    draggableId={banner.id.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <Col
                        xs={24}
                        md={12}
                        lg={8}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Card
                          hoverable
                          style={{
                            borderRadius: 18,
                            boxShadow: "0 15px 45px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div
                            {...provided.dragHandleProps}
                            style={{
                              cursor: "grab",
                              touchAction: "none",
                            }}
                          >
                            <img
                              src={banner.image}
                              alt="banner"
                              style={{
                                width: "100%",
                                height: 180,
                                objectFit: "cover",
                                borderRadius: 12,
                                marginBottom: 15,
                                display: "block",
                                pointerEvents: "none",
                              }}
                            />

                            <Title level={5}>{banner.title || "Untitled"}</Title>

                            <Text type="secondary">
                              Order: {banner.display_order}
                            </Text>

                            <Divider style={{ marginBottom: 12 }} />
                          </div>

                          <Space
                            align="center"
                            style={{
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Switch
                              checked={banner.is_active}
                              onChange={(value) =>
                                handleStatusChange(banner.id, value)
                              }
                            />
                            <Space align="center" size={8}>
                              <Button
                                className="button-action-small"
                                type="default"
                                icon={<EyeOutlined />}
                                shape="circle"
                                htmlType="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBanner(banner);
                                  setViewModalOpen(true);
                                }}
                              />

                              <Popconfirm
                                title="Delete this banner?"
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => handleDelete(banner.id)}
                                getPopupContainer={(node) =>
                                  node?.parentElement || document.body
                                }
                              >
                                <Button
                                  className="button-action-small"
                                  type="default"
                                  danger
                                  shape="circle"
                                  icon={<DeleteOutlined />}
                                  htmlType="button"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Popconfirm>
                            </Space>
                          </Space>
                        </Card>
                      </Col>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Row>
            )}
          </Droppable>
        </DragDropContext>
      </Spin>

      {/* CREATE MODAL */}
      <Modal
        title="Upload Banner"
        open={modalOpen}
        destroyOnClose
        onCancel={() => {
          setModalOpen(false);
          setFile(null);
          form.resetFields();
        }}
        footer={null}
        width={520}
        bodyStyle={{ paddingTop: 8 }}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleCreate}
          initialValues={{ status: false }}
        >
          <Form.Item label="Banner Image" required>
            <div className="banner-manager-image-upload">
              <ImgCrop
                aspect={16 / 9}
                rotationSlider
                beforeCrop={validateBannerBeforeCrop}
              >
                <Upload
                  listType="picture"
                  className="banner-manager-upload-inner"
                  showUploadList={{
                    showPreviewIcon: false,
                    showRemoveIcon: true,
                  }}
                  beforeUpload={async (file) => {
                    let width;
                    let height;
                    try {
                      ({ width, height } = await readImageNaturalSize(file));
                    } catch {
                      message.error("Could not read image dimensions");
                      return Upload.LIST_IGNORE;
                    }

                    if (
                      Math.abs(width / height - BANNER_ASPECT) >
                      BANNER_ASPECT_EPS
                    ) {
                      message.error("Cropped image must be 16:9");
                      return Upload.LIST_IGNORE;
                    }

                    setFile(file);
                    return false;
                  }}
                  maxCount={1}
                  onRemove={() => {
                    setFile(null);
                  }}
                >
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    style={{ borderRadius: 8 }}
                  >
                    Select Image
                  </Button>
                </Upload>
              </ImgCrop>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  marginTop: 14,
                  fontSize: 12,
                  lineHeight: 1.65,
                }}
              >
                Recommended size: 1280×720 to 1920×1080 (16:9).
                <br />
                Maximum file size: 5MB.
              </Text>
            </div>
          </Form.Item>
          <style>{`
            .banner-manager-image-upload {
              background: #fafafa;
              border: 1px solid #f0f0f0;
              border-radius: 12px;
              padding: 16px 16px 14px;
            }
            .banner-manager-upload-inner {
              display: block;
              width: 100%;
            }
            .banner-manager-upload-inner .ant-upload-list-picture {
              margin-top: 14px;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item {
              width: 100%;
              height: auto !important;
              margin-top: 0;
              padding: 10px 12px;
              border-radius: 10px;
              border: 1px solid #e8e8e8;
              background: #fff;
              box-sizing: border-box;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item-thumbnail {
              width: 56px;
              height: 56px;
              line-height: 56px;
              border-radius: 8px;
              overflow: hidden;
              flex-shrink: 0;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item-thumbnail img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item-name {
              flex: 1;
              min-width: 0;
              padding: 0 8px 0 4px;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item-card-actions {
              opacity: 1;
              flex-shrink: 0;
              margin-left: 4px;
            }
            .banner-manager-upload-inner .ant-upload-list-picture .ant-upload-list-item-card-actions-btn {
              margin: 0;
            }
          `}</style>

          <Form.Item name="title" label="Title">
            <Input />
          </Form.Item>

          <Form.Item name="link" label="Redirect URL">
            <Input />
          </Form.Item>

          <Form.Item name="status" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Create Banner
          </Button>
        </Form>
      </Modal>
      {/* VIEW MODAL */}
      <Modal
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={null}
        width={950}
        centered
        bodyStyle={{
          padding: 28,
          maxHeight: "80vh",
          overflowY: "auto",
          background: "#f7f9fc",
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <PictureOutlined style={{ fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              Banner Details
            </span>
          </div>
        }
      >
        {selectedBanner && (
          <>
            {/* IMAGE CARD */}
            <Card
              bordered={false}
              style={{
                borderRadius: 18,
                marginBottom: 24,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                transition: "all 0.3s ease",
              }}
              bodyStyle={{ padding: 20 }}
              className="hover-card"
            >
              <div
                style={{
                  height: 260,
                  borderRadius: 14,
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, #eef2f7 0%, #e3ebf6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={selectedBanner.image}
                  alt="banner"
                  style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </Card>

            {/* INFO GRID */}
            <Row gutter={[20, 20]}>
              {/* TITLE CARD */}
              <Col span={24} md={12} xl={12}>
                <Card
                  bordered={false}
                  className="hover-card"
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  }}
                >
                  <Text type="secondary">Title</Text>
                  <div style={{ fontSize: 17, fontWeight: 600, marginTop: 6 }}>
                    {selectedBanner.title || "—"}
                  </div>
                </Card>
              </Col>

              {/* STATUS CARD */}
              <Col span={24} md={12} xl={12}>
                <Card
                  bordered={false}
                  className="hover-card"
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  }}
                >
                  <Text type="secondary">Status</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag
                      color={selectedBanner.is_active ? "success" : "error"}
                      style={{
                        padding: "6px 18px",
                        borderRadius: 20,
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                    >
                      {selectedBanner.is_active ? "Active" : "Inactive"}
                    </Tag>
                  </div>
                </Card>
              </Col>

              {/* REDIRECT URL */}
              <Col span={24} md={12} xl={12}>
                <Card
                  bordered={false}
                  className="hover-card"
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  }}
                >
                  <Text type="secondary">Redirect URL</Text>
                  <div style={{ marginTop: 8 }}>
                    {selectedBanner.redirect_url ? (
                      <a
                        href={selectedBanner.redirect_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1677ff",
                          fontWeight: 500,
                          wordBreak: "break-all",
                        }}
                      >
                        {selectedBanner.redirect_url}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </Card>
              </Col>

              {/* DISPLAY ORDER */}
              <Col span={24} md={12} xl={12}>
                <Card
                  bordered={false}
                  className="hover-card"
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  }}
                >
                  <Text type="secondary">Display Order</Text>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 6 }}>
                    #{selectedBanner.display_order}
                  </div>
                </Card>
              </Col>

              {/* CREATED DATE */}
              {selectedBanner.created_at && (
                <Col span={24} md={12} xl={12}>
                  <Card
                    bordered={false}
                    className="hover-card"
                    style={{
                      borderRadius: 16,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Text type="secondary">Created At</Text>
                    <div style={{ fontSize: 15, marginTop: 6 }}>
                      {selectedBanner.created_at}
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}
      </Modal>
    </div>
  );
}

export default BannerManager;
